import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LedgerDialog from "@/components/LedgerDialog";

// Pages
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Products from "@/pages/Products";
import Orders from "@/pages/Orders";
import Warehouse from "@/pages/Warehouse";
import CRM from "@/pages/CRM";
import Receipts from "@/pages/Receipts";
import Conversations from "@/pages/Conversations";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import ActivityLog from "@/pages/ActivityLog";
import Returns from "@/pages/Returns";
import Marketing from "@/pages/Marketing";
import Strategy from "@/pages/Strategy";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerPreview from "@/pages/CustomerPreview";
import { useParams } from "react-router-dom";

function CustomerDashboardPreview() {
  const { customerName } = useParams();
  return <CustomerDashboard previewCustomerName={decodeURIComponent(customerName)} />;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route wrapper
function ProtectedRoute({ children, permission, roles }) {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Login />;
  }

  const roleAllowed = !roles || roles.includes(user.role);
  const permAllowed = !permission || hasPermission(permission);

  if (!roleAllowed || !permAllowed) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="text-muted-foreground">You don&apos;t have permission to view this page.</p>
      </div>
    </div>;
  }

  return children;
}

// Alt+L Ledger Shortcut
function LedgerShortcut() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'l') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return <LedgerDialog open={open} onOpenChange={setOpen} />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  useEffect(() => {
    // Initialize default data on first load
    const initData = async () => {
      try {
        await axios.post(`${API}/init-data`);
      } catch (e) {
        console.log("Init data:", e.message);
      }
    };
    initData();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Login />;
  }

  // Customer/Guest-role users see only their dashboard
  if (user.role === 'customer' || user.role === 'guest') {
    return <CustomerDashboard />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>} />
        <Route path="customers" element={<ProtectedRoute permission="customers"><Customers /></ProtectedRoute>} />
        <Route path="products" element={<ProtectedRoute permission="products"><Products /></ProtectedRoute>} />
        <Route path="orders" element={<ProtectedRoute permission="orders"><Orders /></ProtectedRoute>} />
        <Route path="warehouse" element={<ProtectedRoute permission="warehouse"><Warehouse /></ProtectedRoute>} />
        <Route path="crm" element={<ProtectedRoute permission="crm"><CRM /></ProtectedRoute>} />
        <Route path="receipts" element={<ProtectedRoute permission="crm"><Receipts /></ProtectedRoute>} />
        <Route path="returns" element={<ProtectedRoute permission="crm"><Returns /></ProtectedRoute>} />
        <Route path="marketing" element={<ProtectedRoute permission="customers"><Marketing /></ProtectedRoute>} />
        <Route path="strategy" element={<ProtectedRoute roles={["owner", "admin"]}><Strategy /></ProtectedRoute>} />
        <Route path="conversations" element={<ProtectedRoute permission="conversations"><Conversations /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute permission="reports"><Reports /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute permission="settings"><Settings /></ProtectedRoute>} />
        <Route path="activity-log" element={<ProtectedRoute permission="settings"><ActivityLog /></ProtectedRoute>} />
        <Route path="customer-view/:customerName" element={<ProtectedRoute permission="customers"><CustomerDashboardPreview /></ProtectedRoute>} />
        <Route path="customer-preview" element={<ProtectedRoute permission="customers"><CustomerPreview /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen texture-overlay">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <LedgerShortcut />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
